import { useEffect, useRef, useState } from "react";
import { t } from "../../i18n";
import { AgentIcon, agentIcons, defaultAgentIcon } from "./AgentIcon";

const iconNames = Object.keys(agentIcons);

export function AgentIconPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const root = useRef<HTMLDivElement>(null);
  const filtered = iconNames.filter((name) =>
    name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={`button flex min-w-28 items-center gap-2 rounded-lg ${open ? "bg-stone-100 dark:bg-white/10" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <AgentIcon name={value} size={18} className="text-lumen-600" />
        <span className="text-xs">{value || defaultAgentIcon}</span>
        <span className="ml-auto text-xs text-stone-400">⌄</span>
      </button>
      {open && (
        <div className="absolute top-full left-0 z-50 mt-2 w-80 rounded-xl border border-stone-200 bg-white p-2 shadow-xl dark:border-white/10 dark:bg-[#222725]">
          <input
            autoFocus
            className="field mb-2 w-full text-xs"
            placeholder={t("Search icons")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label={t("Search icons")}
          />
          <div
            className="grid max-h-64 grid-cols-8 gap-1 overflow-y-auto"
            role="listbox"
          >
            {filtered.map((name) => (
              <button
                key={name}
                type="button"
                title={name}
                aria-label={name}
                aria-selected={value === name}
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                  setQuery("");
                }}
                className={`grid size-8 place-items-center rounded-lg ${value === name ? "bg-lumen-100 text-lumen-700 ring-2 ring-lumen-500 dark:bg-lumen-900/50 dark:text-lumen-300" : "hover:bg-stone-100 dark:hover:bg-white/10"}`}
              >
                <AgentIcon name={name} size={16} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
